import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { fetchFeaturedArtist } from '../services/galleryService';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';

import { colors, typography, PrimaryButton, SecondaryButton } from '../theme';
import PhotoImage from '../components/PhotoImage';


const HeroTitle = [{ title: "Frame.", subtitle: "A Home for Every Lens." }]

export default function Hero() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [artistData, setArtistData] = useState<{ name: string, title: string, imageUrl: string | null, thumbhash?: string } | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;
        // eslint-disable-next-line react-hooks/set-state-in-effect -- show loading state while fetching the featured artist
        setLoading(true);
        async function loadFeaturedArtist() {
            try {
                const data = await fetchFeaturedArtist();
                if (isMounted) {
                    setArtistData(data);
                }
            } catch (error) {
                console.error("Failed to load featured artist:", error);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        }
        loadFeaturedArtist();
        return () => { isMounted = false; };
    }, []);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                pt: { xs: 16, md: 12 },
                pb: { xs: 8, md: 12 }
            }}
        >
            <Container maxWidth="lg" sx={{ px: { xs: 3, md: 6 } }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: 'center',
                        gap: { xs: 6, md: 12 }
                    }}
                >
                    {/* Left side: Text Content */}
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="h1"
                            sx={{
                                fontFamily: typography.headline,
                                fontSize: { xs: '48px', md: '64px' },
                                fontWeight: 300,
                                color: colors.text,
                                letterSpacing: '-0.02em',
                                lineHeight: 1.1,
                                mb: 3,
                                whiteSpace: { md: 'nowrap' },
                                textWrap: 'balance'
                            }}
                        >
                            {HeroTitle[0].title} <br /> {HeroTitle[0].subtitle}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                fontFamily: typography.ui,
                                fontSize: '18px',
                                color: colors.textSecondary,
                                lineHeight: 1.6,
                                mb: 6,
                                maxWidth: 'auto'
                            }}
                        >
                            {t('hero.description')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <PrimaryButton disableRipple onClick={() => navigate('/gallery')}>{t('hero.exploreGallery')}</PrimaryButton>
                            <SecondaryButton disableRipple onClick={() => navigate('/about')}>{t('hero.about', 'About')}</SecondaryButton>
                        </Box>
                    </Box>

                    {/* Right side: Visual */}
                    <Box sx={{ flex: 1, position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <Box sx={{ position: 'relative', width: '100%', maxWidth: '480px' }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    backgroundColor: colors.borderLight,
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 24px 48px rgba(0,0,0,0.08)',
                                    overflow: 'hidden'
                                }}
                            >
                                {artistData?.imageUrl && (
                                    <PhotoImage
                                        src={artistData.imageUrl}
                                        alt={artistData.name ? `Featured work by ${artistData.name}` : 'Featured photography'}
                                        thumbhash={artistData.thumbhash}
                                        // Nothing above sets a ratio, so without this the
                                        // whole hero shifts when the photograph lands.
                                        reserveSpace
                                        fetchPriority="high"
                                    />
                                )}
                            </Box>

                            {/* Featured Artist Card */}
                            {(loading || artistData) && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: { xs: -24, md: -32 },
                                        left: { xs: 16, md: -64 },
                                        backgroundColor: colors.onPrimary,
                                        padding: '24px 24px',
                                        boxShadow: '0 12px 32px rgba(0,0,0,0.06)',
                                        zIndex: 10,
                                        border: `1px solid ${colors.borderLight}`,
                                        width: 'auto'
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: typography.ui,
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            letterSpacing: '0.1em',
                                            textTransform: 'uppercase',
                                            color: colors.textSecondary,
                                            mb: 1.5
                                        }}
                                    >
                                        {t('hero.featuredArtist')}
                                    </Typography>
                                    {loading ? (
                                        <>
                                            <Box className="skeleton" sx={{ width: '140px', height: '24px', mb: 1.5 }} />
                                            <Box className="skeleton" sx={{ width: '100px', height: '14px' }} />
                                        </>
                                    ) : (
                                        <>
                                            <Typography
                                                sx={{
                                                    fontFamily: typography.headline,
                                                    fontSize: '24px',
                                                    color: colors.text,
                                                    mb: 1
                                                }}
                                            >
                                                {artistData?.name}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: typography.ui,
                                                    fontSize: '14px',
                                                    color: colors.textSecondary,
                                                    fontStyle: 'italic',
                                                    lineHeight: 1.5
                                                }}
                                            >
                                                {artistData?.title}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}
